import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = isSignup
      ? await signup({ email, password, display_name: displayName })
      : await login({ email, password });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error || "Something went wrong");
      return;
    }
    navigate(returnTo);
  };

  return (
    <section
      data-testid={isSignup ? "signup-page" : "login-page"}
      className="max-w-[1080px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-12"
    >
      <div className="hidden md:block">
        <div className="eyebrow mb-4">{isSignup ? "Create account" : "Welcome back"}</div>
        <h1 className="font-serif-display text-4xl text-ink leading-tight">
          {isSignup
            ? "Save your reviews and pick up where you left off."
            : "Sign in to your review history."}
        </h1>
        <p className="mt-5 text-ink-70 leading-relaxed max-w-md">
          Your account keeps a private history of every review — with the memo,
          findings, and exact citations, ready to reopen anytime.
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid={isSignup ? "signup-form" : "login-form"}
        className="paper-card space-y-5"
      >
        <div>
          <div className="eyebrow mb-2">{isSignup ? "Sign up" : "Log in"}</div>
          <div className="font-serif-display text-2xl text-ink">
            {isSignup ? "Create your account" : "Log in"}
          </div>
        </div>

        {isSignup && (
          <label className="block">
            <div className="text-xs font-mono-plex uppercase tracking-widest text-ink-50 mb-1.5">
              Display name (optional)
            </div>
            <input
              data-testid="auth-name-input"
              className="w-full border border-ink-20 px-3 py-2.5 bg-white outline-none focus-ring text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </label>
        )}

        <label className="block">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-ink-50 mb-1.5">
            Email
          </div>
          <input
            data-testid="auth-email-input"
            type="email"
            required
            className="w-full border border-ink-20 px-3 py-2.5 bg-white outline-none focus-ring text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@firm.com"
          />
        </label>

        <label className="block">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-ink-50 mb-1.5">
            Password
          </div>
          <input
            data-testid="auth-password-input"
            type="password"
            required
            minLength={6}
            className="w-full border border-ink-20 px-3 py-2.5 bg-white outline-none focus-ring text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
          />
        </label>

        {err && (
          <div
            data-testid="auth-error"
            className="text-sm text-[var(--severity-red-text)] bg-[var(--severity-red-bg)] border border-[var(--severity-red-border)] px-3 py-2"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          data-testid="auth-submit-btn"
          disabled={busy}
          className="btn-primary focus-ring w-full justify-center"
        >
          {busy ? "Working…" : isSignup ? "Create account" : "Log in"}
        </button>

        <div className="text-sm text-ink-70">
          {isSignup ? (
            <>
              Already have an account?{" "}
              <Link
                to="/login"
                data-testid="auth-switch-login"
                className="text-ink underline underline-offset-4"
              >
                Log in
              </Link>
            </>
          ) : (
            <>
              New here?{" "}
              <Link
                to="/signup"
                data-testid="auth-switch-signup"
                className="text-ink underline underline-offset-4"
              >
                Create an account
              </Link>
            </>
          )}
        </div>
      </form>
    </section>
  );
}
